'use client';

import { useEffect } from 'react';
import { fetchAPI } from '@/core/api/client';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

/**
 * "Koszyki klientów" admin dashboard — best-effort, debounced sync of the visitor's cart contents
 * on every cart change, wherever it happens (product page, product grid, cart page, checkout
 * clearing it). It used to live in the cart page's block only, so a cart showed up in the admin
 * just when the shopper opened /koszyk — anyone who added a product and left never did (dlaszwalni:
 * 5 carts in the first days). Must never block the storefront or surface an error to the shopper.
 */
export function CartTrackingSync() {
    const { currency } = useTranslation();

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        const sync = () => {
            try {
                const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
                let sessionKey = localStorage.getItem('stag_session_id');
                // Nothing tracked yet and nothing to track — no row to create or clear.
                if ((!Array.isArray(cart) || cart.length === 0) && !sessionKey) return;
                if (!sessionKey) {
                    sessionKey = crypto.randomUUID();
                    localStorage.setItem('stag_session_id', sessionKey);
                }
                const isReturning = localStorage.getItem('stag_returning_visitor') === 'true';
                if (!isReturning) localStorage.setItem('stag_returning_visitor', 'true');

                const items = cart.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    image_url: item.image_url,
                }));
                const itemsValue = Math.round(cart.reduce((acc: number, item: any) => acc + (item.price * item.quantity), 0) * 100) / 100;

                fetchAPI('/cart-tracking/sync', {
                    method: 'POST',
                    body: JSON.stringify({
                        session_key: sessionKey,
                        items,
                        items_value: itemsValue,
                        currency: currency || 'PLN',
                        is_returning: isReturning,
                    }),
                }).catch(() => {});
            } catch {
                // localStorage/crypto unavailable (private mode, old browser) — never let this break the cart UI
            }
        };
        const scheduleSync = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(sync, 2000);
        };

        // A cart filled before this tracked every change (or in another tab) is picked up on load.
        try {
            if ((localStorage.getItem('stag-cart') || '[]') !== '[]') scheduleSync();
        } catch { /* no storage */ }
        window.addEventListener('cart-updated', scheduleSync);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('cart-updated', scheduleSync);
        };
    }, [currency]);

    return null;
}
